/**
 * Create Subscription From Pricebook Modal
 *
 * Creates a Stripe subscription from a pricebook for a client.
 * Requires a linked Stripe customer (graph id) on the client.
 */

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { usePricebooks } from '../../services/queries/pricebookQueries';
import { useCreateStripeSubscription } from '../../services/queries/stripeSubscriptionQueries';
import { useStripeCustomerForClientOrAccount } from '../../services/queries/stripeCustomerQueries';
import { useToast } from '../../contexts/ToastContext';

interface CreateSubscriptionFromPricebookModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string | number;
  clientName?: string;
  onSuccess?: (subscriptionId?: string) => void;
}

export const CreateSubscriptionFromPricebookModal: React.FC<CreateSubscriptionFromPricebookModalProps> = ({
  isOpen,
  onClose,
  clientId,
  clientName,
  onSuccess,
}) => {
  const { success, error: showError } = useToast();
  const [pricebookIdOrKey, setPricebookIdOrKey] = useState('');
  const [trialPeriodDays, setTrialPeriodDays] = useState<number | ''>('');

  const { data: stripeCustomer } = useStripeCustomerForClientOrAccount(clientId);
  const { data: pricebooks = [] } = usePricebooks();
  const createSubscription = useCreateStripeSubscription();

  const customerId = stripeCustomer?.id;
  const stripeCustomerId = stripeCustomer?.accountID;
  const hasCustomer = customerId != null || (typeof stripeCustomerId === 'string' && stripeCustomerId.length > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pricebookIdOrKey) {
      showError('Error', 'Please select a pricebook');
      return;
    }
    if (!hasCustomer) {
      showError('Error', 'This client has no linked Stripe customer. Create or link a Stripe customer first.');
      return;
    }

    try {
      const result = await createSubscription.mutateAsync({
        pricebookIdOrKey,
        customerId: customerId != null ? Number(customerId) : undefined,
        stripeCustomerId: typeof stripeCustomerId === 'string' ? stripeCustomerId : undefined,
        items: [],
        trialPeriodDays: trialPeriodDays !== '' ? Number(trialPeriodDays) : undefined,
      });

      const subscriptionId = result.subscriptionId;

      success('Subscription created', subscriptionId ? `Subscription ${subscriptionId} created successfully` : 'Subscription created');
      onSuccess?.(subscriptionId);
      onClose();
      setPricebookIdOrKey('');
      setTrialPeriodDays('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      showError('Error', msg);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-[#1e293b] rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Create Subscription from Pricebook
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {clientName && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Client: <span className="font-medium text-gray-900 dark:text-white">{clientName}</span>
          </p>
        )}

        {!hasCustomer ? (
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 text-amber-800 dark:text-amber-200 text-sm">
            This client has no linked Stripe customer. Use &quot;Create Stripe customer&quot; or &quot;Link existing Stripe customer&quot; first, then create a subscription from a pricebook.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Pricebook
              </label>
              <select
                value={pricebookIdOrKey}
                onChange={(e) => setPricebookIdOrKey(e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
                required
              >
                <option value="">Select a pricebook</option>
                {pricebooks.map((pb: { id?: string | number; name?: string; key?: string }) => (
                  <option key={String(pb.id ?? pb.key)} value={String(pb.id ?? pb.key ?? '')}>
                    {pb.name ?? pb.key ?? String(pb.id)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Trial period (days)
              </label>
              <input
                type="number"
                min={0}
                value={trialPeriodDays}
                onChange={(e) => setTrialPeriodDays(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                placeholder="Optional"
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createSubscription.isPending || !pricebookIdOrKey}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createSubscription.isPending ? 'Creating…' : 'Create Subscription'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
